import React, { useEffect, useState } from "react";
import axios from "axios";
import { Save, Check, AlertCircle, ExternalLink, Key, RefreshCw, Code, Zap, Bell, Chrome, Download, MonitorSmartphone, Loader2, ShieldCheck, Bot, Workflow } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useNotifications } from "../contexts/NotificationContext";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Normalize a US phone number to E.164 format: +1XXXXXXXXXX
 * Strips all non-digit chars, removes leading 1 if 11 digits, prepends +1.
 */
function normalizePhone(raw) {
  const digits = raw.replace(/\D/g, "");
  // If they pasted something with country code like 17255551234
  const national = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (national.length === 0) return "";
  if (national.length > 10) return `+1${national.slice(0, 10)}`;
  return `+1${national}`;
}

function Section({ title, children, icon: Icon }) {
  return (
    <div className="card p-6">
      <div className="flex items-center gap-2.5 mb-5 pb-4 border-b border-slate-100">
        <div className="w-8 h-8 rounded-lg bg-[#FFF0EB] flex items-center justify-center">
          <Icon size={16} className="text-[#FF5C35]" />
        </div>
        <h3 className="font-semibold font-heading text-slate-900">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function AccountSection({ user }) {
  const { setUser } = useAuth();

  // ─── Profile editing state ───
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [saving, setSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState("");

  // ─── Phone change state ───
  const [phoneStep, setPhoneStep] = useState("idle"); // idle | enter | verify | done
  const [newPhone, setNewPhone] = useState("");
  const [phoneMasked, setPhoneMasked] = useState("");
  const [phoneCode, setPhoneCode] = useState("");
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [phoneResend, setPhoneResend] = useState(0);

  // ─── Password change state ───
  const [pwStep, setPwStep] = useState("idle"); // idle | verify | done
  const [pwMasked, setPwMasked] = useState("");
  const [pwCode, setPwCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState("");
  const [pwResend, setPwResend] = useState(0);

  // ─── Helpers ───
  const formatPhone = (raw) => {
    const d = raw.replace(/\D/g, "");
    if (d.length <= 3) return d;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
  };

  const displayPhone = user?.phone
    ? user.phone.replace(/^\+1/, "").replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3")
    : null;

  const startCooldown = (setter) => {
    setter(60);
    const interval = setInterval(() => {
      setter(prev => { if (prev <= 1) { clearInterval(interval); return 0; } return prev - 1; });
    }, 1000);
  };

  // ─── Profile actions ───
  const saveProfile = async () => {
    setSaving(true); setProfileMsg("");
    try {
      const { data } = await axios.put(`${API}/api/auth/profile`, { name, email });
      setUser(data);
      setProfileMsg("Profile updated");
      setEditing(false);
    } catch (e) {
      setProfileMsg(e.response?.data?.detail || "Update failed");
    }
    setSaving(false);
  };

  // ─── Phone actions ───
  const sendPhoneCode = async () => {
    setPhoneLoading(true); setPhoneError("");
    const digits = newPhone.replace(/\D/g, "");
    if (digits.length !== 10) { setPhoneError("Please enter a valid 10-digit phone number"); setPhoneLoading(false); return; }
    try {
      const { data } = await axios.post(`${API}/api/auth/change-phone/send-code`, { new_phone: `+1${digits}` });
      setPhoneMasked(data.masked_phone || "your new number");
      setPhoneStep("verify");
      startCooldown(setPhoneResend);
    } catch (e) { setPhoneError(e.response?.data?.detail || "Failed to send code"); }
    setPhoneLoading(false);
  };

  const confirmPhone = async () => {
    setPhoneError("");
    if (phoneCode.length !== 6) { setPhoneError("Please enter the 6-digit code"); return; }
    setPhoneLoading(true);
    const digits = newPhone.replace(/\D/g, "");
    try {
      await axios.post(`${API}/api/auth/change-phone`, { new_phone: `+1${digits}`, code: phoneCode });
      try { const { data } = await axios.get(`${API}/api/auth/me`); setUser(data); } catch (_) {}
      setPhoneStep("done");
    } catch (e) { setPhoneError(e.response?.data?.detail || "Phone change failed"); }
    setPhoneLoading(false);
  };

  const resetPhone = () => { setPhoneStep("idle"); setNewPhone(""); setPhoneCode(""); setPhoneError(""); };

  // ─── Password actions ───
  const sendPwCode = async () => {
    setPwLoading(true); setPwError("");
    try {
      const { data } = await axios.post(`${API}/api/auth/change-password/send-code`);
      setPwMasked(data.masked_phone || "your phone");
      setPwStep("verify");
      startCooldown(setPwResend);
    } catch (e) { setPwError(e.response?.data?.detail || "Failed to send code"); }
    setPwLoading(false);
  };

  const confirmPw = async () => {
    setPwError("");
    if (newPassword.length < 6) { setPwError("Password must be at least 6 characters"); return; }
    if (newPassword !== confirmPassword) { setPwError("Passwords don't match"); return; }
    if (pwCode.length !== 6) { setPwError("Please enter the 6-digit code"); return; }
    setPwLoading(true);
    try {
      await axios.post(`${API}/api/auth/change-password`, { code: pwCode, new_password: newPassword });
      setPwStep("done");
    } catch (e) { setPwError(e.response?.data?.detail || "Password change failed"); }
    setPwLoading(false);
  };

  const resetPw = () => { setPwStep("idle"); setPwCode(""); setNewPassword(""); setConfirmPassword(""); setPwError(""); };

  // ─── Render ───
  return (
    <Section title="Account" icon={Key}>
      <div className="space-y-0 text-sm">

        {/* ── Profile ── */}
        {editing ? (
          <div className="space-y-3 pb-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Name</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)} className="input text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="input text-sm" />
            </div>
            {profileMsg && <p className={`text-xs ${profileMsg.includes("failed") || profileMsg.includes("use") ? "text-red-600" : "text-green-600"}`}>{profileMsg}</p>}
            <div className="flex gap-2 pt-1">
              <button onClick={saveProfile} disabled={saving} className="btn-primary text-xs px-4 py-1.5">
                {saving ? <><Loader2 size={12} className="animate-spin" /> Saving...</> : <><Save size={12} /> Save</>}
              </button>
              <button onClick={() => { setEditing(false); setName(user?.name || ""); setEmail(user?.email || ""); setProfileMsg(""); }} className="btn-secondary text-xs px-4 py-1.5">Cancel</button>
            </div>
          </div>
        ) : (
          <div className="space-y-0">
            <div className="flex justify-between py-2.5 border-b border-slate-100">
              <span className="text-slate-500">Name</span>
              <span className="font-medium text-slate-900">{user?.name}</span>
            </div>
            <div className="flex justify-between py-2.5 border-b border-slate-100">
              <span className="text-slate-500">Email</span>
              <span className="font-medium text-slate-900">{user?.email}</span>
            </div>
            <div className="flex justify-between items-center py-2.5 border-b border-slate-100">
              <span className="text-slate-500">Phone</span>
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-900">{displayPhone || <span className="text-slate-400">Not set</span>}</span>
                {phoneStep === "idle" && (
                  <button onClick={() => setPhoneStep("enter")} className="text-xs text-[#FF5C35] hover:text-[#E84E29] font-medium">
                    {user?.phone ? "Change" : "Add"}
                  </button>
                )}
              </div>
            </div>
            <div className="flex justify-between items-center py-2.5">
              <span className="text-slate-500">Password</span>
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-900">••••••••</span>
                {pwStep === "idle" && (
                  <button onClick={sendPwCode} disabled={pwLoading} className="text-xs text-[#FF5C35] hover:text-[#E84E29] font-medium">
                    {pwLoading ? "Sending..." : "Change"}
                  </button>
                )}
              </div>
            </div>
            {profileMsg && <p className="text-xs text-green-600 pt-1">{profileMsg}</p>}
            {!phoneStep.startsWith("enter") && !phoneStep.startsWith("verify") && pwStep === "idle" && (
              <button onClick={() => setEditing(true)} className="btn-secondary text-xs px-4 py-1.5 mt-2">Edit Profile</button>
            )}
          </div>
        )}

        {/* ── Phone Change Flow (inline, below profile rows) ── */}
        {phoneStep === "enter" && (
          <div className="border-t border-slate-100 pt-4 mt-2 space-y-3">
            <p className="text-slate-500 text-xs">Enter your {user?.phone ? "new" : ""} phone number. We'll send a verification code to confirm it.</p>
            {phoneError && <p className="text-xs text-red-600">{phoneError}</p>}
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-slate-500 pointer-events-none">+1</span>
              <input
                type="tel"
                value={formatPhone(newPhone)}
                onChange={e => setNewPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                className="input pl-9 text-sm"
                placeholder="(725) 555-1234"
                maxLength={16}
              />
            </div>
            <div className="flex gap-2">
              <button onClick={sendPhoneCode} disabled={phoneLoading || newPhone.replace(/\D/g, "").length !== 10} className="btn-primary text-xs px-4 py-1.5">
                {phoneLoading ? <><Loader2 size={12} className="animate-spin" /> Sending...</> : "Send Code"}
              </button>
              <button onClick={resetPhone} className="btn-secondary text-xs px-4 py-1.5">Cancel</button>
            </div>
          </div>
        )}
        {phoneStep === "verify" && (
          <div className="border-t border-slate-100 pt-4 mt-2 space-y-3">
            <p className="text-slate-500 text-xs">Code sent to <strong className="text-slate-700">{phoneMasked}</strong></p>
            {phoneError && <p className="text-xs text-red-600">{phoneError}</p>}
            <input type="text" value={phoneCode} onChange={e => setPhoneCode(e.target.value.replace(/\D/g, "").slice(0, 6))} className="input text-center text-lg font-mono tracking-[0.2em]" placeholder="000000" maxLength={6} />
            <div className="flex items-center justify-between">
              <button onClick={confirmPhone} disabled={phoneLoading || phoneCode.length !== 6} className="btn-primary text-xs px-4 py-1.5">
                {phoneLoading ? <><Loader2 size={12} className="animate-spin" /> Confirming...</> : <><ShieldCheck size={12} /> Confirm</>}
              </button>
              <button onClick={sendPhoneCode} disabled={phoneResend > 0} className="text-xs text-slate-500 hover:text-slate-700">
                {phoneResend > 0 ? `Resend in ${phoneResend}s` : "Resend code"}
              </button>
            </div>
          </div>
        )}
        {phoneStep === "done" && (
          <div className="border-t border-slate-100 pt-4 mt-2 text-center py-2">
            <ShieldCheck size={20} className="text-green-500 mx-auto mb-1" />
            <p className="font-medium text-green-700 text-xs">Phone number updated!</p>
            <button onClick={resetPhone} className="text-xs text-slate-500 hover:text-slate-700 mt-2 underline">Done</button>
          </div>
        )}

        {/* ── Password Change Flow (inline) ── */}
        {pwStep === "verify" && (
          <div className="border-t border-slate-100 pt-4 mt-2 space-y-3">
            <p className="text-slate-500 text-xs">Code sent to <strong className="text-slate-700">{pwMasked}</strong></p>
            {pwError && <p className="text-xs text-red-600">{pwError}</p>}
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Verification Code</label>
              <input type="text" value={pwCode} onChange={e => setPwCode(e.target.value.replace(/\D/g, "").slice(0, 6))} className="input text-center text-lg font-mono tracking-[0.2em]" placeholder="000000" maxLength={6} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">New Password</label>
              <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="input text-sm" placeholder="Min. 6 characters" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Confirm Password</label>
              <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="input text-sm" placeholder="Confirm new password" />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex gap-2">
                <button onClick={confirmPw} disabled={pwLoading || pwCode.length !== 6 || newPassword.length < 6} className="btn-primary text-xs px-4 py-1.5">
                  {pwLoading ? <><Loader2 size={12} className="animate-spin" /> Changing...</> : <><ShieldCheck size={12} /> Change Password</>}
                </button>
                <button onClick={resetPw} className="btn-secondary text-xs px-4 py-1.5">Cancel</button>
              </div>
              <button onClick={sendPwCode} disabled={pwResend > 0} className="text-xs text-slate-500 hover:text-slate-700">
                {pwResend > 0 ? `Resend in ${pwResend}s` : "Resend code"}
              </button>
            </div>
          </div>
        )}
        {pwError && pwStep === "idle" && <p className="text-xs text-red-600 pt-2">{pwError}</p>}
        {pwStep === "done" && (
          <div className="border-t border-slate-100 pt-4 mt-2 text-center py-2">
            <ShieldCheck size={20} className="text-green-500 mx-auto mb-1" />
            <p className="font-medium text-green-700 text-xs">Password changed successfully!</p>
            <button onClick={resetPw} className="text-xs text-slate-500 hover:text-slate-700 mt-2 underline">Done</button>
          </div>
        )}
      </div>
    </Section>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const { pushSupported, pushEnabled, subscribePush, unsubscribePush } = useNotifications();
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    signalwire_project_id: "",
    signalwire_api_token: "",
    signalwire_space_url: "",
    signalwire_phone_number: "",
    fub_api_key: "",
    anthropic_api_key: "",
    ai_auto_reply_enabled: false,
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [fubStatus, setFubStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");
  const [syncProgress, setSyncProgress] = useState(null);
  const [fubNoteSmsEnabled, setFubNoteSmsEnabled] = useState(false);
  const [fubNoteTimeHandling, setFubNoteTimeHandling] = useState("smart_window");
  const [timezoneUtcOffset, setTimezoneUtcOffset] = useState(-7);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/settings`);
      setSettings(r.data);
      setForm(f => ({
        ...f,
        signalwire_project_id: r.data.signalwire_project_id || "",
        signalwire_space_url: r.data.signalwire_space_url || "",
        signalwire_phone_number: r.data.signalwire_phone_number || "",
      }));
      setForm(prev => ({ ...prev, ai_auto_reply_enabled: r.data.ai_auto_reply_enabled || false }));
      setFubNoteSmsEnabled(r.data.fub_note_sms_enabled || false);
      setFubNoteTimeHandling(r.data.fub_note_time_handling || "smart_window");
      setTimezoneUtcOffset(r.data.timezone_utc_offset != null ? r.data.timezone_utc_offset : -7);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (form.signalwire_project_id !== "") payload.signalwire_project_id = form.signalwire_project_id;
      if (form.signalwire_api_token !== "") payload.signalwire_api_token = form.signalwire_api_token;
      if (form.signalwire_space_url !== "") payload.signalwire_space_url = form.signalwire_space_url;
      if (form.signalwire_phone_number !== "") payload.signalwire_phone_number = form.signalwire_phone_number;
      if (form.fub_api_key !== "") payload.fub_api_key = form.fub_api_key;
      if (form.anthropic_api_key !== "") payload.anthropic_api_key = form.anthropic_api_key;
      payload.fub_note_sms_enabled = fubNoteSmsEnabled;
      payload.fub_note_time_handling = fubNoteTimeHandling;
      payload.timezone_utc_offset = timezoneUtcOffset;
      payload.ai_auto_reply_enabled = form.ai_auto_reply_enabled;
      await axios.put(`${API}/api/settings`, payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      load();
    } catch (e) {
      alert("Save failed");
    }
    setSaving(false);
  };

  const testFUB = async () => {
    setTesting(true); setFubStatus(null);
    try {
      // If user typed a new key in the input, test that key directly (no save needed)
      if (form.fub_api_key && form.fub_api_key.trim()) {
        const r = await axios.post(`${API}/api/fub/test-key`, { api_key: form.fub_api_key.trim() });
        setFubStatus(r.data);
      } else {
        // No new key typed — test the saved key
        const r = await axios.get(`${API}/api/fub/test`);
        setFubStatus(r.data);
      }
    } catch (e) { setFubStatus({ connected: false, message: "Test failed" }); }
    setTesting(false);
  };

  const syncFUB = async () => {
    setSyncing(true); setSyncMsg(""); setSyncProgress("Connecting to FollowUp Boss...");
    try {
      const r = await axios.post(`${API}/api/fub/sync`);
      setSyncMsg(r.data.message);
    } catch (e) {
      setSyncMsg(e.response?.data?.detail || "Sync failed");
    }
    setSyncing(false);
    setSyncProgress(null);
  };

  const appUrl = window.location.origin;

  return (
    <div className="p-4 md:p-8 max-w-2xl" data-testid="settings-page">
      <div className="mb-6">
        <h1 className="text-xl md:text-2xl font-semibold font-heading text-slate-900">Settings</h1>
        <p className="text-slate-500 text-sm mt-0.5">Configure your integrations and credentials</p>
      </div>

      <div className="space-y-5">
        {/* FUB Integration */}
        <Section title="FollowUp Boss Integration" icon={Key}>
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 bg-[#FFF0EB] rounded-lg border border-[#D1D5DB]">
              <AlertCircle size={15} className="text-[#FF5C35] mt-0.5 shrink-0" />
              <div className="text-sm text-[#0F1419]">
                <p className="font-medium">How to get your API key</p>
                <p className="text-xs mt-0.5 text-[#FF5C35]">In FUB: Admin &rarr; API Keys &amp; Lead Email &rarr; Copy your API key. <a href="https://help.followupboss.com" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-0.5">Help docs <ExternalLink size={10} /></a></p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">FUB API Key</label>
              <input
                type="password"
                value={form.fub_api_key}
                onChange={e => setForm(f => ({ ...f, fub_api_key: e.target.value }))}
                className="input"
                placeholder={settings?.fub_configured ? "••••••••••••" : "Enter your FollowUp Boss API key"}
                data-testid="fub-api-key-input"
              />
              {settings?.fub_configured && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <Check size={11} /> API key configured. Enter a new one to update.
                </p>
              )}
            </div>

            {fubStatus && (
              <div className={`px-3 py-2 rounded-md text-sm border ${fubStatus.connected ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"}`} data-testid="fub-test-result">
                {fubStatus.connected
                  ? <><Check size={13} className="inline mr-1" />Connected to: <strong>{fubStatus.account}</strong></>
                  : fubStatus.message}
              </div>
            )}

            {syncProgress && (
              <div className="flex items-center gap-2 px-3 py-2 bg-[#FFF0EB] rounded-md border border-[#D1D5DB] text-sm text-[#0F1419]">
                <span className="w-3 h-3 border border-[#FF5C35] border-t-transparent rounded-full animate-spin shrink-0" />
                {syncProgress}
              </div>
            )}

            {syncMsg && (
              <div className={`px-3 py-2 rounded-md text-sm border ${syncMsg.includes("Successfully") ? "bg-green-50 text-green-700 border-green-200" : "bg-amber-50 text-amber-700 border-amber-200"}`} data-testid="sync-status">
                {syncMsg}
              </div>
            )}

            <div className="flex gap-2">
              <button onClick={testFUB} disabled={testing} className="btn-secondary" data-testid="test-fub-btn">
                {testing
                  ? <span className="flex items-center gap-1.5"><span className="w-3 h-3 border border-slate-400 border-t-transparent rounded-full animate-spin" />Testing...</span>
                  : "Test Connection"}
              </button>
              <button onClick={syncFUB} disabled={syncing} className="btn-primary" data-testid="sync-fub-settings-btn">
                <RefreshCw size={13} className={syncing ? "animate-spin" : ""} />
                {syncing ? "Syncing..." : "Sync Contacts Now"}
              </button>
            </div>
            <p className="text-xs text-slate-400">Syncs all contacts from your FollowUp Boss account. May take a minute for large accounts.</p>
          </div>
        </Section>

        {/* FUB Embedded Widget */}
        <Section title="FUB Embedded Widget" icon={Code}>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Add Relay as an embedded app inside FollowUp Boss so agents can text leads directly from the lead profile — without leaving FUB.
            </p>
            <div className="space-y-3">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Widget URL</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-slate-100 border border-slate-200 rounded px-3 py-2 text-slate-800 font-mono truncate" data-testid="widget-url">
                    {appUrl}/fub-widget
                  </code>
                  <button
                    onClick={() => navigator.clipboard.writeText(`${appUrl}/fub-widget`)}
                    className="btn-secondary py-1.5 px-3 text-xs shrink-0"
                    data-testid="copy-widget-url"
                  >
                    Copy
                  </button>
                </div>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-xs text-slate-600 space-y-1.5">
                <p className="font-semibold text-slate-700">How to install in FollowUp Boss:</p>
                <ol className="list-decimal list-inside space-y-1 text-slate-600">
                  <li>In FUB, go to <strong>Admin &rarr; Integrations &rarr; Embedded Apps</strong></li>
                  <li>Click <strong>Create an embedded app</strong></li>
                  <li>Paste the widget URL above as the <strong>App URL</strong></li>
                  <li>Name it <strong>"Relay"</strong> and save</li>
                  <li>Enable it — it will appear on every lead profile sidebar</li>
                </ol>
              </div>
            </div>
          </div>
        </Section>

        {/* Chrome Extension */}
        <Section title="Chrome Extension for FUB" icon={Chrome}>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Install the Relay Chrome extension to add a mass blast button directly into FollowUp Boss's People page toolbar. Select and filter contacts in FUB, then send a blast without leaving the page.
            </p>
            <div className="flex items-start gap-3 p-3 bg-[#FFF0EB] rounded-lg border border-[#D1D5DB]">
              <MonitorSmartphone size={15} className="text-[#FF5C35] mt-0.5 shrink-0" />
              <div className="text-sm text-[#0F1419]">
                <p className="font-medium">What it does</p>
                <p className="text-xs mt-0.5 text-[#FF5C35]">Adds an SMS icon to FUB's toolbar on the People page. Click it to open a full blast modal — filter by stage, tags, or Smart Lists, compose your message with merge fields, and send to hundreds of contacts instantly.</p>
              </div>
            </div>
            <div>
              <a
                href={`${API}/api/extension/download`}
                download
                className="btn-primary inline-flex items-center gap-2 w-auto px-5"
              >
                <Download size={15} />
                Download Extension (.zip)
              </a>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-xs text-slate-600 space-y-1.5">
              <p className="font-semibold text-slate-700">How to install (Developer Mode):</p>
              <ol className="list-decimal list-inside space-y-1 text-slate-600">
                <li>Download the ZIP file above and <strong>unzip it</strong> to a permanent folder</li>
                <li>Open Chrome and go to <strong>chrome://extensions</strong></li>
                <li>Enable <strong>Developer mode</strong> (toggle in the top-right corner)</li>
                <li>Click <strong>"Load unpacked"</strong> and select the unzipped folder</li>
                <li>Navigate to FollowUp Boss → People page — the SMS icon will appear in the toolbar</li>
              </ol>
            </div>
            <p className="text-xs text-slate-400">The extension will be available on the Chrome Web Store soon. For now, use the manual install method above.</p>
          </div>
        </Section>

        {/* SignalWire */}
        <Section title="SignalWire SMS" icon={Zap}>
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 bg-violet-50 rounded-lg border border-violet-200">
              <Zap size={15} className="text-violet-600 mt-0.5 shrink-0" />
              <div className="text-sm text-violet-700">
                <p className="font-medium">~50% cheaper than Twilio</p>
                <p className="text-xs mt-0.5 text-violet-600">
                  Sign up at <a href="https://signalwire.com" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-0.5">signalwire.com <ExternalLink size={10} /></a> — get your Project ID, API Token, and Space URL from your dashboard.
                </p>
              </div>
            </div>
            {settings?.sms_configured ? (
              <div className="flex items-center gap-1.5 px-3 py-2 bg-green-50 rounded-md border border-green-200 text-sm text-green-700">
                <Check size={13} /> SignalWire configured — real SMS sending enabled
              </div>
            ) : (
              <div className="flex items-center gap-1.5 px-3 py-2 bg-amber-50 rounded-md border border-amber-200 text-sm text-amber-700">
                <AlertCircle size={13} /> Not configured — SMS runs in simulation mode
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Project ID</label>
                <input
                  value={form.signalwire_project_id}
                  onChange={e => setForm(f => ({ ...f, signalwire_project_id: e.target.value }))}
                  className="input font-mono text-sm"
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  data-testid="sw-project-id-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Space URL</label>
                <input
                  value={form.signalwire_space_url}
                  onChange={e => setForm(f => ({ ...f, signalwire_space_url: e.target.value }))}
                  className="input font-mono text-sm"
                  placeholder="your-space.signalwire.com"
                  data-testid="sw-space-url-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">API Token</label>
              <input
                type="password"
                value={form.signalwire_api_token}
                onChange={e => setForm(f => ({ ...f, signalwire_api_token: e.target.value }))}
                className="input font-mono text-sm"
                placeholder={settings?.sms_configured ? "••••••••••••" : "PTxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"}
                data-testid="sw-api-token-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Phone Number</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-slate-500 pointer-events-none">+1</span>
                <input
                  value={form.signalwire_phone_number.replace(/^\+1/, "")}
                  onChange={e => {
                    const formatted = normalizePhone(e.target.value);
                    setForm(f => ({ ...f, signalwire_phone_number: formatted }));
                  }}
                  onBlur={() => {
                    if (form.signalwire_phone_number) {
                      setForm(f => ({ ...f, signalwire_phone_number: normalizePhone(f.signalwire_phone_number) }));
                    }
                  }}
                  className="input pl-9"
                  placeholder="7255551234"
                  maxLength={14}
                  data-testid="sw-phone-input"
                />
              </div>
              <p className="text-xs text-slate-400 mt-1">US numbers only. Auto-formats to +1XXXXXXXXXX (E.164).</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-xs text-slate-600 space-y-1">
              <p className="font-semibold text-slate-700">A2P 10DLC Registration (Required for bulk SMS)</p>
              <p>Nathan Petrosian's team needs to register their brand + campaign in the SignalWire dashboard using their business EIN. This is a one-time setup that unlocks high-volume sending and carrier deliverability.</p>
              <a href="https://signalwire.com/10dlc" target="_blank" rel="noopener noreferrer" className="text-[#FF5C35] underline inline-flex items-center gap-0.5 mt-1">10DLC registration guide <ExternalLink size={9} /></a>
            </div>
          </div>
        </Section>

        {/* Notifications */}
        <Section title="Notifications" icon={Bell}>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-900">Push Notifications</p>
                <p className="text-xs text-slate-500 mt-0.5">Get notified when leads reply to your messages</p>
              </div>
              {pushSupported ? (
                pushEnabled ? (
                  <button onClick={unsubscribePush} className="btn-secondary text-xs py-1.5 px-3" data-testid="disable-push-btn">
                    <Check size={12} className="text-green-600" /> Enabled
                  </button>
                ) : (
                  <button onClick={subscribePush} className="btn-primary text-xs py-1.5 px-3" data-testid="enable-push-btn">
                    <Bell size={12} /> Enable
                  </button>
                )
              ) : (
                <span className="text-xs text-slate-400">Not supported in this browser</span>
              )}
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-900">In-App Sound</p>
                <p className="text-xs text-slate-500 mt-0.5">Play a sound when new messages arrive</p>
              </div>
              <span className="text-xs text-green-600 flex items-center gap-1"><Check size={12} /> Always on</span>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-900">Unread Badge</p>
                <p className="text-xs text-slate-500 mt-0.5">Show unread count on sidebar and browser tab</p>
              </div>
              <span className="text-xs text-green-600 flex items-center gap-1"><Check size={12} /> Always on</span>
            </div>
          </div>
        </Section>

        {/* AI Auto-Reply */}
        <Section title="AI Auto-Reply (Claude)" icon={Bot}>
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 bg-[#FFF0EB] rounded-lg border border-[#D1D5DB]">
              <Bot size={15} className="text-[#FF5C35] mt-0.5 shrink-0" />
              <div className="text-sm text-[#0F1419]">
                <p className="font-medium">Intelligent SMS reply classification</p>
                <p className="text-xs mt-0.5 text-[#FF5C35]">When enabled on a mass blast, Claude AI automatically classifies replies (confirmed, declined, stop, question) and can auto-respond to confirmed leads. Get your API key at <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-0.5">console.anthropic.com <ExternalLink size={10} /></a></p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Anthropic API Key</label>
              <input
                type="password"
                value={form.anthropic_api_key}
                onChange={e => setForm(f => ({ ...f, anthropic_api_key: e.target.value }))}
                className="input font-mono text-sm"
                placeholder={settings?.ai_configured ? "••••••••••••" : "sk-ant-api03-..."}
                data-testid="anthropic-api-key-input"
              />
              {settings?.ai_configured ? (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <Check size={11} /> AI auto-reply ready. Enter a new key to update.
                </p>
              ) : (
                <p className="text-xs text-slate-400 mt-1">Optional — only needed if you want AI-powered reply classification on mass blasts.</p>
              )}
            </div>

            {/* Universal Agent Bot toggle */}
            <div className="border-t border-slate-100 pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-700">Agent Bot Auto-Reply</p>
                  <p className="text-xs text-slate-500 mt-0.5">AI reads conversation context and sends smart replies to ALL inbound messages. Tagged with [Agent Bot] so contacts know it's automated.</p>
                </div>
                <button
                  onClick={() => {
                    const newVal = !form.ai_auto_reply_enabled;
                    setForm(f => ({ ...f, ai_auto_reply_enabled: newVal }));
                  }}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0 ml-3 ${form.ai_auto_reply_enabled ? "bg-[#FF5C35]" : "bg-slate-300"}`}
                >
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${form.ai_auto_reply_enabled ? "translate-x-4" : "translate-x-0.5"}`} />
                </button>
              </div>
              {form.ai_auto_reply_enabled && !settings?.ai_configured && !form.anthropic_api_key && (
                <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                  <Bot size={11} /> Add your Anthropic API key above to enable Agent Bot.
                </p>
              )}
            </div>
          </div>
        </Section>

        {/* FUB Note SMS Detection */}
        <Section title="FUB Campaign SMS Detection" icon={Workflow}>
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 bg-[#FFF0EB] rounded-lg border border-[#D1D5DB]">
              <Workflow size={15} className="text-[#FF5C35] mt-0.5 shrink-0" />
              <div className="text-sm text-[#0F1419]">
                <p className="font-medium">Auto-detect SMS notes from FUB Automations</p>
                <p className="text-xs mt-0.5 text-[#FF5C35]">When enabled, Relay polls FUB every 2 minutes for new notes with <code className="bg-white px-1 rounded">[SMS]</code> prefix and sends them as text messages. Works with both FUB Automations and Action Plans — just add <code className="bg-white px-1 rounded">[SMS]</code> at the start of any Add Note step.</p>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200">
              <div>
                <p className="text-sm font-medium text-slate-900">Enable FUB Note SMS Detection</p>
                <p className="text-xs text-slate-500 mt-0.5">Polls every 2 min for notes starting with [SMS], [9am], [15min], etc.</p>
              </div>
              <button
                onClick={async () => {
                  const next = !fubNoteSmsEnabled;
                  setFubNoteSmsEnabled(next);
                  try { await axios.put(`${API}/api/settings`, { fub_note_sms_enabled: next }); } catch (e) { setFubNoteSmsEnabled(!next); }
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${fubNoteSmsEnabled ? "bg-[#FF5C35]" : "bg-slate-300"}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${fubNoteSmsEnabled ? "translate-x-6" : "translate-x-1"}`} />
              </button>
            </div>

            {fubNoteSmsEnabled && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Your Timezone</label>
                  <select
                    value={timezoneUtcOffset}
                    onChange={async (e) => {
                      const val = Number(e.target.value);
                      setTimezoneUtcOffset(val);
                      try { await axios.put(`${API}/api/settings`, { timezone_utc_offset: val }); } catch (err) { console.error(err); }
                    }}
                    className="input text-sm"
                  >
                    <option value={-10}>Hawaii (UTC-10)</option>
                    <option value={-9}>Alaska (UTC-9)</option>
                    <option value={-8}>Pacific (UTC-8)</option>
                    <option value={-7}>Mountain / Arizona (UTC-7)</option>
                    <option value={-6}>Central (UTC-6)</option>
                    <option value={-5}>Eastern (UTC-5)</option>
                    <option value={-4}>Atlantic (UTC-4)</option>
                  </select>
                  <p className="text-xs text-slate-400 mt-1.5">All scheduled times (e.g. [9am], [430pm]) and smart window hours use this timezone.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Time Handling</label>
                  <select
                    value={fubNoteTimeHandling}
                    onChange={async (e) => {
                      const val = e.target.value;
                      setFubNoteTimeHandling(val);
                      try { await axios.put(`${API}/api/settings`, { fub_note_time_handling: val }); } catch (err) { console.error(err); }
                    }}
                    className="input text-sm"
                  >
                    <option value="smart_window">Smart Window (9am–8pm, hold outside hours)</option>
                    <option value="honor_prefix">Honor Prefix (send at time specified in note, e.g. [9am])</option>
                    <option value="immediate">Immediate (send as soon as detected, ignore time prefixes)</option>
                  </select>
                  <p className="text-xs text-slate-400 mt-1.5">Smart Window holds messages outside business hours until 9:15am next day. Honor Prefix respects [9am], [2pm] time tags in notes. Immediate sends everything right away.</p>
                </div>
              </>
            )}

            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-xs font-medium text-slate-700 mb-2">Supported prefixes in FUB notes:</p>
              <div className="grid grid-cols-2 gap-1.5 text-xs text-slate-600">
                <div><code className="bg-white px-1 rounded text-[#FF5C35]">[SMS]</code> Send immediately</div>
                <div><code className="bg-white px-1 rounded text-[#FF5C35]">[9am]</code> Schedule for 9am</div>
                <div><code className="bg-white px-1 rounded text-[#FF5C35]">[430pm]</code> Schedule for 4:30pm</div>
                <div><code className="bg-white px-1 rounded text-[#FF5C35]">[2:30pm]</code> Schedule for 2:30pm</div>
                <div><code className="bg-white px-1 rounded text-[#FF5C35]">[30m]</code> Delay 30 minutes</div>
                <div><code className="bg-white px-1 rounded text-[#FF5C35]">[3hrs]</code> Delay 3 hours</div>
                <div><code className="bg-white px-1 rounded text-[#FF5C35]">[15min]</code> Delay 15 minutes</div>
                <div><code className="bg-white px-1 rounded text-[#FF5C35]">[SMS][9am]</code> Combined</div>
              </div>
              <p className="text-xs text-slate-400 mt-2">FUB merge fields like <code className="bg-white px-0.5 rounded">%contact_first_name%</code> are automatically resolved. Notes can be created directly on contacts or via FUB Automations.</p>
            </div>
          </div>
        </Section>

        {/* AI Lead Intent */}
        <Section title="AI Lead Intent & Auto Draft" icon={Bot}>
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 bg-[#FFF0EB] rounded-lg border border-[#D1D5DB]">
              <Bot size={15} className="text-[#FF5C35] mt-0.5 shrink-0" />
              <div className="text-sm text-[#0F1419]">
                <p className="font-medium">AI-powered lead follow-up drafts</p>
                <p className="text-xs mt-0.5 text-[#FF5C35]">When enabled, new leads and stage changes trigger AI analysis. Claude classifies intent (buyer/seller, urgency, stage) and drafts a personalized follow-up text for your review. Requires Anthropic API key above.</p>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200">
              <div>
                <p className="text-sm font-medium text-slate-900">Enable AI Lead Intent</p>
                <p className="text-xs text-slate-500 mt-0.5">Triggers on FUB webhooks: new lead, lead updated, stage change</p>
              </div>
              <button
                onClick={async () => {
                  const next = !(settings?.ai_intent_enabled);
                  try {
                    await axios.put(`${API}/api/settings`, { ai_intent_enabled: next });
                    load();
                  } catch (e) { console.error(e); }
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings?.ai_intent_enabled ? "bg-[#FF5C35]" : "bg-slate-300"}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings?.ai_intent_enabled ? "translate-x-6" : "translate-x-1"}`} />
              </button>
            </div>

            {settings?.ai_intent_enabled && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Brokerage Name</label>
                  <input
                    type="text"
                    defaultValue={settings?.ai_intent_brokerage_name || ""}
                    onBlur={async (e) => {
                      try { await axios.put(`${API}/api/settings`, { ai_intent_brokerage_name: e.target.value }); } catch (err) { console.error(err); }
                    }}
                    className="input text-sm"
                    placeholder="e.g. Petrosian Real Estate Group"
                  />
                  <p className="text-xs text-slate-400 mt-1">Used in AI-drafted messages so the lead knows who's texting.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Rate Limit (days between AI outbound per lead)</label>
                  <select
                    defaultValue={settings?.ai_intent_rate_limit_days || 7}
                    onChange={async (e) => {
                      try { await axios.put(`${API}/api/settings`, { ai_intent_rate_limit_days: parseInt(e.target.value) }); } catch (err) { console.error(err); }
                    }}
                    className="input text-sm"
                    style={{ maxWidth: 200 }}
                  >
                    <option value={3}>3 days</option>
                    <option value={5}>5 days</option>
                    <option value={7}>7 days</option>
                    <option value={14}>14 days</option>
                    <option value={30}>30 days</option>
                  </select>
                  <p className="text-xs text-slate-400 mt-1">Prevents AI from drafting too many messages to the same lead.</p>
                </div>
              </>
            )}
          </div>
        </Section>

        {/* Account — profile, phone, password all in one */}
        <AccountSection user={user} />

        <div className="flex justify-end">
          <button onClick={save} disabled={saving} className="btn-primary px-6" data-testid="save-settings-btn">
            {saving
              ? <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving...</span>
              : saved
                ? <><Check size={15} /> Saved!</>
                : <><Save size={15} /> Save Settings</>}
          </button>
        </div>
      </div>
    </div>
  );
}
